import {
  customerCallListSchema,
  customerCallSchema,
  customerNoteListSchema,
  customerNoteSchema,
  customerTaskCompletionSchema,
  taskListSchema,
  taskSchema,
  type CreateCallInput,
  type CreateNoteInput,
  type CreateTaskInput,
  type CustomerCall,
  type CustomerNote,
  type SetTaskStatusInput,
  type Task,
  type TaskList,
  type TaskStatus,
  type UpdateNoteInput,
  type UpdateTaskInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * A customer's notes, calls and follow-up tasks, under `/customers/:id/…`.
 *
 * Notes are editable and soft-deleted, calls are append-only, and a task's open status drives the
 * follow-up date.
 *
 * Every response is validated against the SAME schema the server validated its output with, so a
 * field that quietly changes shape on one side fails loudly on the other rather than rendering
 * as `undefined` three components deep.
 */

/** Who the notes, calls and tasks are about: a customer, the only contact record. */
export type ActivitySubject = { kind: 'customer'; id: string };

export type ActivityNote = CustomerNote;
export type ActivityCall = CustomerCall;

function base(subject: ActivitySubject): string {
  return `/customers/${subject.id}`;
}

/* ----------------------------------------------------------------------------- Notes ------- */

export async function listNotes(
  client: ApiClient,
  subject: ActivitySubject,
): Promise<{ items: ActivityNote[] }> {
  return await client.get(`${base(subject)}/notes`, { schema: customerNoteListSchema });
}

export async function createNote(
  client: ApiClient,
  subject: ActivitySubject,
  input: CreateNoteInput,
): Promise<ActivityNote> {
  return await client.post(`${base(subject)}/notes`, input, { schema: customerNoteSchema });
}

export async function updateNote(
  client: ApiClient,
  subject: ActivitySubject,
  noteId: string,
  input: UpdateNoteInput,
): Promise<ActivityNote> {
  const path = `${base(subject)}/notes/${noteId}`;
  return await client.patch(path, input, { schema: customerNoteSchema });
}

export async function deleteNote(
  client: ApiClient,
  subject: ActivitySubject,
  noteId: string,
): Promise<void> {
  await client.del(`${base(subject)}/notes/${noteId}`);
}

/* ----------------------------------------------------------------------------- Calls ------- */

export async function listCalls(
  client: ApiClient,
  subject: ActivitySubject,
): Promise<{ items: ActivityCall[] }> {
  return await client.get(`${base(subject)}/calls`, { schema: customerCallListSchema });
}

/** There is deliberately no update and no delete: the server refuses both. */
export async function logCall(
  client: ApiClient,
  subject: ActivitySubject,
  input: CreateCallInput,
): Promise<ActivityCall> {
  return await client.post(`${base(subject)}/calls`, input, { schema: customerCallSchema });
}

/* ----------------------------------------------------------------------------- Tasks ------- */

export async function listTasks(client: ApiClient, subject: ActivitySubject): Promise<TaskList> {
  return await client.get(`${base(subject)}/tasks`, { schema: taskListSchema });
}

export async function createTask(
  client: ApiClient,
  subject: ActivitySubject,
  input: CreateTaskInput,
): Promise<Task> {
  return await client.post(`${base(subject)}/tasks`, input, { schema: taskSchema });
}

export async function updateTask(
  client: ApiClient,
  subject: ActivitySubject,
  taskId: string,
  input: UpdateTaskInput,
): Promise<Task> {
  return await client.patch(`${base(subject)}/tasks/${taskId}`, input, { schema: taskSchema });
}

export async function setTaskStatus(
  client: ApiClient,
  subject: ActivitySubject,
  taskId: string,
  status: TaskStatus,
): Promise<Task> {
  const input: SetTaskStatusInput = { status };
  return await client.post(`${base(subject)}/tasks/${taskId}/status`, input, {
    schema: taskSchema,
  });
}

export async function deleteTask(
  client: ApiClient,
  subject: ActivitySubject,
  taskId: string,
): Promise<void> {
  await client.del(`${base(subject)}/tasks/${taskId}`);
}

/** What `POST /tasks/:id/complete` answers with: the closed task, and the call if one was made. */
export interface TaskCompletion {
  task: Task;
  call: ActivityCall | null;
}

/**
 * Ticking a task off and logging the call that closed it, in one request.
 *
 * The call is optional — a task can turn out not to have needed one — and when it is present the
 * server commits both together, so the screen can never show a done task beside a missing call.
 */
export async function completeTask(
  client: ApiClient,
  subject: ActivitySubject,
  taskId: string,
  call?: CreateCallInput,
): Promise<TaskCompletion> {
  const path = `${base(subject)}/tasks/${taskId}/complete`;
  const body = call ? { call } : {};
  return await client.post(path, body, { schema: customerTaskCompletionSchema });
}
